import { adminUrlBuilder } from '@app/api/utils';
import {
  ClientRoleEnum,
  CreatePaymeSellerDto,
  IBranch,
  IBranchInput,
  ICalendarDefaults,
  ICreateEmployeeInput,
  IInvoiceIntegrationConfig,
  IPaymentIntegrationConfig,
  ISettings,
  ISettingsEmployees,
  ISettingsGeneral,
  TSettingsContent,
  TSettingsLandingSite,
  TSettingsMarketing,
  TSettingsPayments,
  TSettingsScheduling
} from '@torim-app/types';

export class SettingsApi {
  static readonly path = 'settings';

  static async getSettings(): Promise<ISettings> {
    const url = adminUrlBuilder({}, SettingsApi.path);
    const errorMessage = 'Failed to get Settings';
    try {
      const response = await fetch(url, {
        method: 'get',
        headers: {
          'content-type': 'application/json'
        }
      });

      if (!response.ok) throw errorMessage;
      return await response.json();
    } catch (e) {
      console.error(e);
      throw errorMessage;
    }
  }

  static async updateGeneralPageSettings(
    body: Partial<Pick<ISettingsGeneral, 'fullName' | 'profileImage'>>
  ): Promise<void> {
    const url = adminUrlBuilder({}, `${SettingsApi.path}/general`);
    const errorMessage = 'Failed to update Settings';
    try {
      const response = await fetch(url, {
        method: 'put',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok)
        throw errorMessage;

    } catch (e) {
      console.error(e);
      throw errorMessage;
    }
  }

  static async inviteEmployee(
    email: string,
    fullName: string
  ): Promise<void> {
    const url = adminUrlBuilder({}, `${SettingsApi.path}/employees`);
    const errorMessage = 'Failed to invite user';
    const body = { email, fullName };
    try {
      const response = await fetch(url, {
        method: 'post',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) throw errorMessage;
    } catch (e) {
      console.error(e);
      throw errorMessage;
    }
  }

  /**
   * Create an employee end-to-end (SuperTokens account + client) with a chosen password.
   * Returns the created client's userId so the caller can jump straight into its profile.
   * Rejects with the backend error message (seat limit / duplicate email) when available.
   */
  static async createEmployee(
    body: ICreateEmployeeInput
  ): Promise<{ userId: string }> {
    const url = adminUrlBuilder({}, `${SettingsApi.path}/employees/create`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.error || 'Failed to create employee');
    }
    return await response.json();
  }

  static async deactivateEmployee(
    email: string
  ): Promise<void> {
    const url = adminUrlBuilder({}, `${SettingsApi.path}/employees`);
    const errorMessage = 'Failed to deactivate user';
    const body = { email };
    try {
      const response = await fetch(url, {
        method: 'delete',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) throw errorMessage;
    } catch (e) {
      console.error(e);
      throw errorMessage;
    }
  }

  static async updateEmployeeRole(
    userId: string,
    role: ClientRoleEnum
  ): Promise<void> {
    const url = adminUrlBuilder({}, `${SettingsApi.path}/employees/role`);
    const errorMessage = 'Failed to update employee role';
    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify({ userId, role })
      });

      if (!response.ok) throw errorMessage;
    } catch (e) {
      console.error(e);
      throw errorMessage;
    }
  }

  /** Toggle whether an employee may see customer phone numbers / e-mails. Managers only. */
  static async updateEmployeeContactAccess(
    userId: string,
    canViewCustomerContact: boolean
  ): Promise<void> {
    const url = adminUrlBuilder({}, `${SettingsApi.path}/employees/contact-access`);
    const errorMessage = 'Failed to update employee contact access';
    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify({ userId, canViewCustomerContact })
      });

      if (!response.ok) throw errorMessage;
    } catch (e) {
      console.error(e);
      throw errorMessage;
    }
  }

  /**
   * Toggle whether an employee sees the whole branch calendar (their colleagues at the
   * same branch) rather than only their own. Managers only.
   */
  static async updateEmployeeBranchCalendarAccess(
    userId: string,
    canViewBranchCalendar: boolean
  ): Promise<void> {
    const url = adminUrlBuilder({}, `${SettingsApi.path}/employees/branch-calendar-access`);
    const errorMessage = 'Failed to update employee branch calendar access';
    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify({ userId, canViewBranchCalendar })
      });

      if (!response.ok) throw errorMessage;
    } catch (e) {
      console.error(e);
      throw errorMessage;
    }
  }

  /**
   * Set what an employee's calendar opens on — which view, and whether it starts on
   * their own appointments or the whole branch. Managers only.
   */
  static async updateEmployeeCalendarDefaults(
    userId: string,
    calendarDefaults: ICalendarDefaults
  ): Promise<void> {
    const url = adminUrlBuilder({}, `${SettingsApi.path}/employees/calendar-defaults`);
    const errorMessage = 'Failed to update employee calendar defaults';
    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify({ userId, calendarDefaults })
      });

      if (!response.ok) throw errorMessage;
    } catch (e) {
      console.error(e);
      throw errorMessage;
    }
  }

  static async updateSettingsContent(
    body: TSettingsContent
  ): Promise<void> {
    const url = adminUrlBuilder({}, SettingsApi.path);
    const response = await fetch(`${url}/content`, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok)
      throw `Failed to updateSettingsContent`;
  }

  static async updateEmployeesSettings(
    body: Pick<ISettingsEmployees, 'allowEmployeeToCreateServices'>
  ): Promise<void> {
    const url = adminUrlBuilder({}, SettingsApi.path);
    const response = await fetch(`${url}/employees`, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok)
      throw `Failed to updateSettingsContent`;
  }

  static async updateSettingsScheduling(
    body: TSettingsScheduling
  ): Promise<void> {
    const url = adminUrlBuilder({}, SettingsApi.path);
    const response = await fetch(`${url}/scheduling`, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok)
      throw `Failed to updateSettingsScheduling`;
  }

  static async updateSettingsPayment(
    body: TSettingsPayments
  ): Promise<void> {
    const url = adminUrlBuilder({}, SettingsApi.path);
    const response = await fetch(`${url}/payment`, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok)
      throw `Failed to updateSettingsPayment`;
  }


  static async updateSettingsAddPaymentIntegration(
    body: Pick<IPaymentIntegrationConfig, 'credentials' | 'provider'>
  ): Promise<void> {
    const url = adminUrlBuilder({}, SettingsApi.path);
    const response = await fetch(`${url}/payment/payment-provider`, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok)
      throw `Failed to updateSettingsAddPaymentIntegration`;
  }

  static async getPrefilledInfo(): Promise<{
    phoneNumber: string;
    email: string;
  }> {
    const url = adminUrlBuilder({}, SettingsApi.path);
    const response = await fetch(`${url}/payment/prefilled-data`, {
      method: 'get',
    });

    if (!response.ok)
      throw `Failed to getPrefilledInfo`;

    return await response.json();
  }

  static async updateSettingsAddInvoiceIntegration(
    body: Pick<IInvoiceIntegrationConfig, 'credentials' | 'provider'> & { businessType?: 'murshe' | 'patur' }
  ): Promise<void> {
    const url = adminUrlBuilder({}, SettingsApi.path);
    const response = await fetch(`${url}/payment/invoice-provider`, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok)
      throw `Failed to updateSettingsAddInvoiceIntegration`;
  }

  static async createPaymeSeller(
    sellerData: CreatePaymeSellerDto
  ): Promise<void> {
    const url = adminUrlBuilder({}, SettingsApi.path);
    const response = await fetch(`${url}/payment/payme/create-seller`, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(sellerData)
    });

    if (!response.ok)
      throw `Failed to createPaymeSeller`;
  }

  static async validateInvoiceApp(): Promise<{hasInvoices: boolean}> {
    const url = adminUrlBuilder({}, SettingsApi.path);
    const response = await fetch(`${url}/payment/payme/invoices`, {
      method: 'get',
      headers: { 'content-type': 'application/json' },
    });

    if (!response.ok)
      throw `Failed to validateInvoiceApp`;

    return response.json();
  }

  static async updateSettingsLandingSite(
    body: TSettingsLandingSite
  ): Promise<void> {
    const url = adminUrlBuilder({}, SettingsApi.path);
    const response = await fetch(`${url}/landing-site`, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok)
      throw `Failed to updateSettingsLandingSite`;
  }

  static async updateSettingsMarketing(
    body: TSettingsMarketing
  ): Promise<void> {
    const url = adminUrlBuilder({}, SettingsApi.path);
    const response = await fetch(`${url}/marketing`, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok)
      throw `Failed to updateSettingsMarketing`;
  }

  static async exchangeWhatsappToken(
    body: { code: string; redirectUri?: string; wabaId?: string; phoneNumberId?: string }
  ): Promise<{ success: boolean }> {
    const url = adminUrlBuilder({}, `${SettingsApi.path}/whatsapp-connect/exchange-token`);
    const response = await fetch(url, {
      method: 'post',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok)
      throw 'Failed to exchange WhatsApp token';

    return response.json();
  }

  static async toggleAdminAi(enabled: boolean): Promise<{ success: boolean }> {
    const url = adminUrlBuilder({}, `${SettingsApi.path}/whatsapp-connect/admin-ai-toggle`);
    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ enabled }),
    });

    if (!response.ok)
      throw 'Failed to toggle Admin AI';

    return response.json();
  }

  static async toggleWhatsappAi(enabled: boolean): Promise<{ success: boolean }> {
    const url = adminUrlBuilder({}, `${SettingsApi.path}/whatsapp-connect/ai-toggle`);
    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ enabled }),
    });

    if (!response.ok)
      throw 'Failed to toggle WhatsApp AI';

    return response.json();
  }

  static async getWhatsappConnectionStatus(): Promise<{
    connected: boolean;
    displayName?: string;
    phoneNumberId?: string;
    connectedAt?: string;
    aiEnabled?: boolean;
  }> {
    const url = adminUrlBuilder({}, `${SettingsApi.path}/whatsapp-connect/status`);
    const response = await fetch(url, {
      method: 'get',
      headers: { 'content-type': 'application/json' },
    });

    if (!response.ok)
      throw 'Failed to get WhatsApp connection status';

    return response.json();
  }

  static async disconnectWhatsapp(): Promise<void> {
    const url = adminUrlBuilder({}, `${SettingsApi.path}/whatsapp-connect`);
    const response = await fetch(url, {
      method: 'delete',
      headers: { 'content-type': 'application/json' },
    });

    if (!response.ok)
      throw 'Failed to disconnect WhatsApp';
  }

  static async getWhatsappTemplatesStatus(): Promise<{ reminder: boolean; waitingList: boolean; appointmentConfirmation: boolean }> {
    const url = adminUrlBuilder({}, `${SettingsApi.path}/whatsapp-connect/templates-status`);
    const response = await fetch(url, {
      method: 'get',
      headers: { 'content-type': 'application/json' },
    });

    if (!response.ok)
      throw 'Failed to get WhatsApp templates status';

    return response.json();
  }

  static async getBranches(): Promise<IBranch[]> {
    const url = adminUrlBuilder({}, `${SettingsApi.path}/branches`);
    const response = await fetch(url, {
      method: 'get',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) throw 'Failed to get branches';
    return response.json();
  }

  static async createBranch(body: IBranchInput): Promise<IBranch> {
    const url = adminUrlBuilder({}, `${SettingsApi.path}/branches`);
    const response = await fetch(url, {
      method: 'post',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw 'Failed to create branch';
    return response.json();
  }

  static async updateBranch(branchId: string, body: IBranchInput): Promise<IBranch> {
    const url = adminUrlBuilder({}, `${SettingsApi.path}/branches/${branchId}`);
    const response = await fetch(url, {
      method: 'put',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw 'Failed to update branch';
    return response.json();
  }

  static async deleteBranch(branchId: string): Promise<void> {
    const url = adminUrlBuilder({}, `${SettingsApi.path}/branches/${branchId}`);
    const response = await fetch(url, {
      method: 'delete',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) throw 'Failed to delete branch';
  }

  static async updateUserNotifications(
    userId: string
  ): Promise<void> {
    const url = adminUrlBuilder({}, SettingsApi.path + '/notifications');
    const errorMessage = 'Failed to update notifications settings';
    try {
      const response = await fetch(url, {
        method: 'put',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify({ userId })
      });

      if (!response.ok)
        throw errorMessage;

    } catch (e) {
      console.error(e);
      throw errorMessage;
    }
  }
}
